import { Injectable, OnDestroy } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { tap, catchError, switchMap, shareReplay } from "rxjs/operators";

interface CacheEntry<T> {
	data: T;
	timestamp: number;
}

interface CacheConfig {
	duration?: number; // Cache duration in milliseconds
	refreshOnExpiry?: boolean; // Whether to refresh data when cache expires
	shareResults?: boolean; // Whether to share results across subscribers
}

@Injectable({
	providedIn: "root",
})
export class CacheService implements OnDestroy {
	private cache = new Map<string, CacheEntry<any>>();
	private activeRequests = new Map<string, Observable<any>>();
	private defaultCacheDuration = 5 * 60 * 1000; // 5 minutes
	private maxCacheSize = 100; // Maximum number of cache entries
	private accessOrder: string[] = []; // Track access order for LRU
	private cleanupInterval: any;
	private cleanupPeriod = 60 * 1000; // Run cleanup every minute

	constructor() {
		// Start periodic cleanup
		this.startPeriodicCleanup();
	}

	/**
	 * Get data from cache or fetch if not available/expired
	 * @param key Unique cache key
	 * @param fetchFn Function that returns an Observable of the data
	 * @param config Cache configuration
	 */
	get<T>(key: string, fetchFn: () => Observable<T>, config: CacheConfig = {}): Observable<T> {
		const {
			duration = this.defaultCacheDuration,
			refreshOnExpiry = true,
			shareResults = true,
		} = config;

		// Check if there's an active request for this key
		const activeRequest = this.activeRequests.get(key);
		if (activeRequest) {
			return activeRequest;
		}

		// Check if data exists in cache and is still valid
		const cachedEntry = this.cache.get(key);
		if (cachedEntry && !this.isExpired(cachedEntry.timestamp, duration)) {
			this.updateAccessOrder(key);
			return of(cachedEntry.data);
		}

		// If expired and refresh is disabled, return cached data
		if (cachedEntry && !refreshOnExpiry) {
			this.updateAccessOrder(key);
			return of(cachedEntry.data);
		}

		// Fetch new data
		let request$ = fetchFn().pipe(
			tap((data) => {
				this.set(key, data);
				this.activeRequests.delete(key);
			}),
			catchError((error) => {
				this.activeRequests.delete(key);
				// If there's cached data and fetch fails, return cached data
				if (cachedEntry) {
					console.warn(
						`Failed to refresh cache for key: ${key}, using stale data`,
						error,
					);
					return of(cachedEntry.data);
				}
				return throwError(() => error);
			}),
		);

		// Share the request if configured
		if (shareResults) {
			request$ = request$.pipe(shareReplay(1));
		}

		this.activeRequests.set(key, request$);
		return request$;
	}

	/**
	 * Get data from cache or fetch with custom refresh logic
	 * @param key Unique cache key
	 * @param fetchFn Function that returns an Observable of the data
	 * @param refreshTrigger$ Observable that triggers cache refresh
	 * @param config Cache configuration
	 */
	getWithRefresh<T>(
		key: string,
		fetchFn: () => Observable<T>,
		refreshTrigger$: Observable<any>,
		config: CacheConfig = {},
	): Observable<T> {
		return refreshTrigger$.pipe(
			switchMap(() => this.get(key, fetchFn, { ...config, refreshOnExpiry: true })),
		);
	}

	/**
	 * Set data in cache
	 * @param key Cache key
	 * @param data Data to cache
	 */
	set<T>(key: string, data: T): void {
		// Check if we need to evict entries
		if (!this.cache.has(key) && this.cache.size >= this.maxCacheSize) {
			this.evictLRU();
		}

		this.cache.set(key, {
			data,
			timestamp: Date.now(),
		});

		this.updateAccessOrder(key);
	}

	/**
	 * Remove specific key from cache
	 * @param key Cache key to remove
	 */
	remove(key: string): void {
		this.cache.delete(key);
		this.activeRequests.delete(key);
		this.removeFromAccessOrder(key);
	}

	/**
	 * Clear all cache entries
	 */
	clear(): void {
		this.cache.clear();
		this.activeRequests.clear();
		this.accessOrder = [];
	}

	/**
	 * Clear cache entries matching a pattern
	 * @param pattern Regex pattern or string prefix
	 */
	clearPattern(pattern: string | RegExp): void {
		const regex = pattern instanceof RegExp ? pattern : new RegExp(`^${pattern}`);
		const keysToDelete: string[] = [];

		this.cache.forEach((_, key) => {
			if (regex.test(key)) {
				keysToDelete.push(key);
			}
		});

		keysToDelete.forEach((key) => this.remove(key));
	}

	/**
	 * Check if a cache entry exists and is valid
	 * @param key Cache key
	 * @param duration Optional custom duration to check against
	 */
	has(key: string, duration?: number): boolean {
		const entry = this.cache.get(key);
		if (!entry) return false;

		const checkDuration = duration || this.defaultCacheDuration;
		return !this.isExpired(entry.timestamp, checkDuration);
	}

	/**
	 * Get cache statistics
	 */
	getStats(): { size: number; keys: string[]; activeRequests: number } {
		return {
			size: this.cache.size,
			keys: Array.from(this.cache.keys()),
			activeRequests: this.activeRequests.size,
		};
	}

	private isExpired(timestamp: number, duration: number): boolean {
		return Date.now() - timestamp > duration;
	}

	/**
	 * Update access order for LRU tracking
	 */
	private updateAccessOrder(key: string): void {
		// Remove key from current position
		this.removeFromAccessOrder(key);
		// Add key to the end (most recently used)
		this.accessOrder.push(key);
	}

	/**
	 * Remove key from access order
	 */
	private removeFromAccessOrder(key: string): void {
		const index = this.accessOrder.indexOf(key);
		if (index > -1) {
			this.accessOrder.splice(index, 1);
		}
	}

	/**
	 * Evict least recently used entry
	 */
	private evictLRU(): void {
		if (this.accessOrder.length > 0) {
			const lruKey = this.accessOrder[0];
			this.remove(lruKey);
		}
	}

	/**
	 * Start periodic cleanup of expired entries
	 */
	private startPeriodicCleanup(): void {
		this.cleanupInterval = setInterval(() => {
			this.cleanupExpiredEntries();
		}, this.cleanupPeriod);
	}

	/**
	 * Clean up expired cache entries
	 */
	private cleanupExpiredEntries(): void {
		const keysToRemove: string[] = [];

		this.cache.forEach((entry, key) => {
			if (this.isExpired(entry.timestamp, this.defaultCacheDuration)) {
				keysToRemove.push(key);
			}
		});

		keysToRemove.forEach((key) => this.remove(key));
	}

	/**
	 * Destroy cleanup interval (for cleanup on service destroy)
	 */
	ngOnDestroy(): void {
		if (this.cleanupInterval) {
			clearInterval(this.cleanupInterval);
		}
	}
}

/**
 * Build a cache key from prefix and parameters
 */
export function buildCacheKey(prefix: string, ...params: (string | number | boolean)[]): string {
	const sanitized = params.map((p) => String(p).replace(/[^a-zA-Z0-9-_]/g, "_"));
	return [prefix, ...sanitized].join(":");
}

/**
 * Build a cache key from prefix and object properties
 */
export function buildCacheKeyFromObject(prefix: string, obj: Record<string, any>): string {
	const sorted = Object.keys(obj)
		.sort()
		.map((key) => `${key}=${obj[key]}`);
	return buildCacheKey(prefix, ...sorted);
}
